import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h2 className="font-heading text-xl font-extrabold text-neutral-900">{title}</h2>
    {children}
  </div>
);

const Bullets = ({ items }) => (
  <ul className="list-disc space-y-1.5 pl-6 marker:text-[#ED8B00]">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Seo title="Privacy Policy" description="Read the van Gelder, Inc. privacy policy covering how we collect, use, and protect information submitted through our website." />
      <PageHero title="Privacy Policy" testId="privacy-hero" />
      <section className="mx-auto max-w-3xl px-4 py-14 text-neutral-600 leading-relaxed space-y-10">
        <p className="text-sm text-neutral-400" data-testid="privacy-updated">Last Updated: 8/3/2026</p>

        <Section title="1. Introduction">
          <p>
            van Gelder, Inc. (“we,” “us,” or “our”) is committed to protecting your privacy. This
            Privacy Policy explains how we collect, use, disclose, and safeguard your information when
            you visit our website, www.vangelder-inc.com (the “Site”), or use our services.
          </p>
          <p>
            By accessing or using the Site, you acknowledge that you have read and understood this
            Privacy Policy.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p>We may collect information about you in several ways.</p>
          <h3 className="font-semibold text-neutral-800">Personal Information</h3>
          <p>
            When you voluntarily submit a form through our Site, including contact forms, quote
            requests, sample requests, consultation scheduling forms, or project inquiries, we may
            collect information such as:
          </p>
          <Bullets
            items={[
              "Full name",
              "Email address",
              "Phone number",
              "Company name and job title",
              "Mailing or shipping address",
              "Project details, including project type, size, timeline, and budget",
              "Any other information you choose to provide",
            ]}
          />
          <h3 className="font-semibold text-neutral-800">Automatically Collected Information</h3>
          <p>
            When you access the Site, we may automatically collect certain device and usage
            information, including:
          </p>
          <Bullets
            items={[
              "IP address",
              "Browser type",
              "Operating system",
              "Device type",
              "Pages visited",
              "Time spent on individual pages",
              "Referring website or source",
              "General interaction and navigation data",
            ]}
          />
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We may use the information we collect to:</p>
          <Bullets
            items={[
              "Respond to inquiries and fulfill requests",
              "Process sample, product information, and quote requests",
              "Schedule consultations, meetings, or service calls",
              "Communicate with you about projects, products, services, or account-related matters",
              "Send relevant product information and marketing communications, where permitted or with your consent",
              "Improve, maintain, and optimize our Site, products, and services",
              "Analyze website traffic and user behavior",
              "Protect against fraudulent, unauthorized, or unlawful activity",
              "Comply with applicable legal and regulatory obligations",
            ]}
          />
        </Section>

        <Section title="4. How We Share Your Information">
          <p>
            van Gelder, Inc. does not sell, trade, or rent your personal information to third parties.
          </p>
          <p>
            We may share information with trusted vendors, contractors, and service providers that
            assist us with operating our website, conducting our business, processing requests,
            communicating with customers, or providing services. These parties are expected to use the
            information only for the services they provide to us and to maintain appropriate
            confidentiality and security.
          </p>
          <p>
            We may also disclose information when required by law, legal process, or government
            request, or when we believe disclosure is necessary to:
          </p>
          <Bullets
            items={[
              "Protect the rights, property, or safety of van Gelder, Inc.",
              "Protect our customers, employees, or other individuals",
              "Investigate suspected fraud or unlawful activity",
              "Enforce our agreements, policies, or terms",
            ]}
          />
        </Section>

        <Section title="5. Cookies and Tracking Technologies">
          <p>
            We may use cookies, web beacons, analytics tools, and similar technologies to enhance your
            experience on the Site.
          </p>
          <p>Cookies are small data files stored on your device that may help us:</p>
          <Bullets
            items={[
              "Remember your preferences",
              "Understand how visitors use the Site",
              "Measure website performance",
              "Improve navigation and functionality",
              "Deliver relevant communications or content",
            ]}
          />
          <p>
            You can manage or disable cookies through your browser settings. Please note that disabling
            certain cookies may affect the availability or functionality of some Site features.
          </p>
        </Section>

        <Section title="6. Data Security">
          <p>
            We implement reasonable administrative, technical, and physical safeguards designed to
            protect personal information from unauthorized access, use, disclosure, alteration, or
            destruction.
          </p>
          <p>
            However, no method of transmission over the Internet and no method of electronic storage is
            completely secure. Although we work to protect your personal information, we cannot
            guarantee its absolute security.
          </p>
        </Section>

        <Section title="7. Data Retention">
          <p>We retain personal information only for as long as reasonably necessary to:</p>
          <Bullets
            items={[
              "Fulfill the purposes for which the information was collected",
              "Provide requested products or services",
              "Maintain appropriate business and transaction records",
              "Comply with legal, tax, accounting, or regulatory obligations",
              "Resolve disputes",
              "Enforce our agreements and policies",
            ]}
          />
          <p>
            When personal information is no longer required, we may delete, anonymize, or securely
            dispose of it in accordance with our data-retention practices.
          </p>
        </Section>

        <Section title="8. Your Privacy Rights">
          <p>
            Depending on your location and applicable law, you may have certain rights regarding your
            personal information, including the right to:
          </p>
          <Bullets
            items={[
              "Request access to the personal information we maintain about you",
              "Receive a copy of certain personal information",
              "Request that inaccurate or incomplete information be corrected",
              "Request deletion of your personal information",
              "Withdraw consent where processing is based on consent",
              "Opt out of marketing communications",
              "Request information about how your personal information is collected, used, or shared",
            ]}
          />
          <p>These rights may be subject to certain legal limitations or exceptions.</p>
          <p>
            To submit a privacy-related request, please contact us at{" "}
            <a href="mailto:sales@vangelder-inc.com" className="font-semibold text-[#ED8B00] hover:underline">
              sales@vangelder-inc.com
            </a>
            . We may need to verify your identity before completing your request.
          </p>
          <p>
            You may opt out of marketing emails at any time by following the unsubscribe instructions
            included in the communication or by contacting us directly. You may still receive
            non-promotional communications related to active requests, transactions, projects, or
            business relationships.
          </p>
        </Section>

        <Section title="9. Third-Party Links">
          <p>Our Site may contain links to websites or services operated by third parties.</p>
          <p>
            van Gelder, Inc. is not responsible for the privacy, security, content, or data-handling
            practices of third-party websites. We encourage you to review the privacy policies of any
            external website before providing personal information.
          </p>
        </Section>

        <Section title="10. Children's Privacy">
          <p>
            Our Site is intended for business and commercial audiences and is not directed to children
            under the age of 13.
          </p>
          <p>
            We do not knowingly collect personal information from children under 13. If we learn that we
            have collected personal information from a child under 13, we will take reasonable steps to
            delete it promptly.
          </p>
        </Section>

        <Section title="11. Changes to This Privacy Policy">
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices,
            services, technologies, or legal obligations.
          </p>
          <p>
            Changes will be posted on this page with a revised “Last Updated” date. Your continued use
            of the Site after an updated Privacy Policy is posted indicates your acknowledgment of the
            revised policy.
          </p>
        </Section>

        <Section title="12. Contact Us">
          <p>
            For questions, concerns, or requests regarding this Privacy Policy or our data practices,
            please contact:
          </p>
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-5 text-neutral-700">
            <p className="font-heading font-bold text-neutral-900">van Gelder, Inc.</p>
            <p>
              Email:{" "}
              <a href="mailto:sales@vangelder-inc.com" className="font-semibold text-[#ED8B00] hover:underline">
                sales@vangelder-inc.com
              </a>
            </p>
            <p>Phone: 1-706-602-4186</p>
            <p>Website: www.vangelder-inc.com</p>
          </div>
        </Section>
      </section>
    </div>
  );
}
